import { useState, useEffect, useMemo } from 'react';
import { Dropdown } from '@/modules/Dropdown';
import { Input } from '@/modules/Input';
import { InnerButton } from '@/modules/InnerButton';
import {
  FilterColumnOption,
  FilterOperator,
  FILTER_OPERATORS,
  ColumnDataType,
} from '../../types/filter';
import { cn } from '@/shared/utils/cn';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
interface FilterFormProps {
  columns: FilterColumnOption[];
  onAddFilter: (filter: {
    columnId: string;
    columnLabel: string;
    operator: FilterOperator;
    value: string | number | boolean | (string | number)[] | null;
    value2?: string | number | boolean | null;
    dataType?: ColumnDataType;
  }) => void;
  className?: string;
}

export const FilterForm = ({ columns, onAddFilter, className }: FilterFormProps) => {
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | ''>('');
  const [value, setValue] = useState<string>('');
  const [value2, setValue2] = useState<string>('');

  // Obtener el tipo de dato de la columna seleccionada
  const selectedColumnData = useMemo(
    () => columns.find((col) => col.value === selectedColumn),
    [columns, selectedColumn]
  );

  // Filtrar operadores según el tipo de datos de la columna
  const availableOperators = useMemo(() => {
    if (!selectedColumnData) return [];

    return FILTER_OPERATORS.filter((op) => op.dataTypes.includes(selectedColumnData.dataType)).map(
      (op) => ({
        value: op.value,
        label: op.label,
      })
    );
  }, [selectedColumnData]);

  // Resetear operador y valores cuando cambia la columna
  useEffect(() => {
    setSelectedOperator('');
    setValue('');
    setValue2('');
  }, [selectedColumn]);

  // Resetear valores cuando cambia el operador
  useEffect(() => {
    setValue('');
    setValue2('');
  }, [selectedOperator]);

  // Verificar si el filtro está completo y listo para agregar
  const isFilterComplete = useMemo(() => {
    if (!selectedColumn || !selectedOperator) return false;

    const operatorConfig = FILTER_OPERATORS.find((op) => op.value === selectedOperator);
    if (!operatorConfig) return false;

    if (!operatorConfig.requiresValue) return true;
    if (operatorConfig.requiresSecondValue) {
      return value.trim() !== '' && value2.trim() !== '';
    }
    return value.trim() !== '';
  }, [selectedColumn, selectedOperator, value, value2]);

  const handleAdd = () => {
    if (!isFilterComplete || !selectedColumnData) return;

    onAddFilter({
      columnId: selectedColumn,
      columnLabel: selectedColumnData.label,
      operator: selectedOperator as FilterOperator,
      value: value,
      value2: value2 || undefined,
      dataType: selectedColumnData.dataType,
    });

    // Resetear formulario
    setSelectedColumn('');
    setSelectedOperator('');
    setValue('');
    setValue2('');
  };

  const operatorConfig = FILTER_OPERATORS.find((op) => op.value === selectedOperator);

  return (
    <div className={cn('ui-flex ui-flex-col ui-gap-4', className)}>
      {/* Selector de columna y botón agregar */}
      <div className="ui-flex ui-items-end ui-gap-2">
        <Dropdown
          field={{ label: 'Columna', placeholder: 'Selecciona una columna', size: 'sm' }}
          items={{
            options: columns.map((col) => ({ value: col.value, label: col.label })),
            valueName: 'label',
            idName: 'value',
            defaultOptionSelected: false,
          }}
          values={{ value: selectedColumn }}
          events={{ onValueChange: (val) => setSelectedColumn(val as string) }}
          menuSearch={{ searchable: true }}
        />

        <InnerButton
          theme="solid"
          color="secondary"
          size="xs"
          iconProps={{ icon: faUser }}
          onClick={handleAdd}
          disabled={!isFilterComplete}
          tooltipProps={{ label: 'Agregar filtro2', position: 'bottom' }}
          pill="sm"
        />
      </div>

      {/* Selector de operador (solo visible si hay columna seleccionada) */}
      {selectedColumn && (
        <Dropdown
          field={{ label: 'Operador', placeholder: 'Selecciona un operador', size: 'sm' }}
          items={{
            options: availableOperators,
            valueName: 'label',
            idName: 'value',
            defaultOptionSelected: false,
          }}
          values={{ value: selectedOperator }}
          events={{ onValueChange: (val) => setSelectedOperator(val as FilterOperator) }}
        />
      )}

      {/* Inputs de valor (solo visible si el operador requiere valor) */}
      {selectedOperator && operatorConfig?.requiresValue && (
        <div className="ui-flex ui-gap-2">
          <Input
            label="Valor"
            placeholder="Ingresa el valor"
            value={value}
            onValueChange={setValue}
            size="xs"
            fullWidth
            type={selectedColumnData?.dataType === 'number' ? 'number' : 'text'}
          />

          {operatorConfig.requiresSecondValue && (
            <Input
              label="Valor 2"
              placeholder="Ingresa el segundo valor"
              value={value2}
              onValueChange={setValue2}
              size="xs"
              fullWidth
              type={selectedColumnData?.dataType === 'number' ? 'number' : 'text'}
            />
          )}
        </div>
      )}
    </div>
  );
};
